import { ActionsPatientActivityTable } from "../../../core/redux/slices/slicePatientActivityTable";
import { ApiPatientActivity } from "../../../core/apis";
import { ApiSchemaActivity } from "../../../models/api/activity";
import { ApiSchemaPatient } from "../../../models/api/patient";
import { AppRouteUi } from "../../../config/appRoute";
import { EditableCard } from "../../../components/editableCard.component";
import { Pagination, Table } from "rsuite";
import { PaginationEntriesInput } from "../../../components/paginationEntries";
import { useAppDispatch, useAppSelector } from "../../../core/redux";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import {
  DateCell,
  ImageCell,
  SeeTasksCell,
  TextCell,
} from "../../../components/tableItem.component";
import { searchTableData, sortTableData } from "../../../utils/tableFilter";

type TableData = ApiSchemaActivity & {
  name: string;
  sl: number;
};

const { Column, HeaderCell, Cell } = Table;

type Props = {
  data: ApiSchemaPatient | undefined;
  isValidating: boolean;
};

export const PatientActivityLogCard = ({
  data: patientData,
  isValidating: patientValidating,
}: Props) => {
  const { patientId } = useParams<{ patientId: string }>();
  const { data, isValidating } = ApiPatientActivity.Patient.Hook(
    patientId,
    patientData?.companyId
  );
  const [tableData, setTableData] = useState<TableData[]>([]);
  const { pageNumber, pageSize, isLoading, searchKey } = useAppSelector(
    (state) => state.patientActivityTable
  );

  const dispatch = useAppDispatch();

  const [sortBy, setSortBy] = useState<keyof TableData>("sl");
  const [sortAsc, setSortAsc] = useState(true);

  useEffect(() => {
    dispatch(ActionsPatientActivityTable.goToPage(1));
    dispatch(ActionsPatientActivityTable.setEntryCount(10));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(
    () =>
      setTableData(
        data?.map((activity, index) => ({
          sl: (pageNumber - 1) * pageSize + index + 1,
          name:
            activity.caregiver?.firstName + " " + activity.caregiver?.lastName,
          photo: activity.caregiver?.profilePic,
          tasks: activity.taskList,
          phone: activity.caregiver?.phoneNumber,
          ...activity,
        })) ?? []
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data]
  );

  const filteredTableData = useMemo(() => {
    return sortTableData(
      searchTableData(tableData, searchKey),
      sortBy,
      sortAsc
    );
  }, [searchKey, sortAsc, sortBy, tableData]);

  return (
    <EditableCard
      link={`${AppRouteUi.Patient.Root()}/update/${patientId}`}
      title="Activity Log"
      isValidating={isValidating || patientValidating}
    >
      <div>
        <div className="p-2 d-flex align-item-center">
          <div className="align-self-center me-2">Show</div>
          <PaginationEntriesInput
            onChange={(value) =>
              dispatch(
                ActionsPatientActivityTable.setEntryCount(value as number)
              )
            }
          />
          <div className="align-self-center ms-2">entries</div>
        </div>

        <Table
          autoHeight
          data={filteredTableData.filter(
            (val, idx) =>
              idx >= (pageNumber - 1) * pageSize && idx < pageNumber * pageSize
          )}
          loading={isValidating || isLoading}
          renderEmpty={() => (
            <div className="d-flex justify-content-center text-muted align-items-center h-100 text-center p-5">
              <h3 className="fw-light">No Data Available</h3>
            </div>
          )}
          sortColumn={sortBy}
          sortType={sortAsc ? "asc" : "desc"}
          onSortColumn={(sortBy, sortAsc) => {
            setSortBy(sortBy as keyof TableData);
            setSortAsc(sortAsc === "asc");
          }}
        >
          <Column align="center" width={60} sortable>
            <HeaderCell className="fw-bold text-black">
              <span title="S/L">S/L</span>
            </HeaderCell>
            <TextCell dataKey="sl" />
          </Column>

          <Column align="center" width={70}>
            <HeaderCell className="fw-bold text-black">
              <span title="Photo">Photo</span>
            </HeaderCell>
            <ImageCell dataKey="photo" />
          </Column>

          <Column flexGrow={1} sortable>
            <HeaderCell className="fw-bold text-black">
              <span title="Caregiver Name">Caregiver Name</span>
            </HeaderCell>
            <TextCell dataKey="name" />
          </Column>

          <Column flexGrow={1}>
            <HeaderCell className="fw-bold text-black">
              <span title="Phone Number">Phone Number</span>
            </HeaderCell>
            <TextCell dataKey="phone" />
          </Column>

          <Column flexGrow={1} sortable>
            <HeaderCell className="fw-bold text-black">
              <span title="Start Time">Start Time</span>
            </HeaderCell>
            <DateCell dataKey="startTime" time />
          </Column>

          <Column flexGrow={1} sortable>
            <HeaderCell className="fw-bold text-black">
              <span title="End Time">End Time</span>
            </HeaderCell>
            <DateCell dataKey="endTime" time />
          </Column>

          <Column>
            <HeaderCell className="fw-bold text-black">
              <span title="Task List">Task List</span>
            </HeaderCell>
            <Cell>
              {(rowData: TableData) => (
                <SeeTasksCell taskDetailsList={patientData?.taskDetailsList} />
              )}
            </Cell>
          </Column>
        </Table>

        {data && (
          <div className="d-flex justify-content-end mt-3">
            <Pagination
              total={filteredTableData.length}
              pages={Math.ceil(filteredTableData.length / pageSize)}
              maxButtons={6}
              next={true}
              prev={true}
              activePage={pageNumber}
              onSelect={(page) =>
                dispatch(ActionsPatientActivityTable.goToPage(page))
              }
            />
          </div>
        )}
      </div>
    </EditableCard>
  );
};
